import React, { useState, useEffect } from 'react';
import { Alert } from 'antd';
import clsx from 'clsx';
import ProductFinderInfo from 'src/types/ProductFinderInfo';
import { Sorting } from 'src/types/Sorting';
import Spin from 'src/components/common/Spin';
import ProductListTable from './ProductListTable/ProductListTable';
import { arrayWrap } from 'src/utils/arrayWrap';
import { objectEntries } from 'src/utils/objectEntries';
import { CustomFilters } from '../AdvancedFiltersSider/CustomFilters';
import { useProductListFetch } from '../hooks';
import { useSearchProductsStateContext } from 'src/stateProviders/useSearchProductsStateContext';
import useQueryUrlParams from 'src/hooks/useQueryUrlParams';
import useSourceOwnerId from 'src/hooks/useSourceOwnerId';
import styles from './ProductList.module.less';

export type ProductListProps = {
  sorting: Sorting<ProductFinderInfo>;
  onSortingChange: (sorting: Sorting<ProductFinderInfo>) => void;
};

export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_NUMBER = 1;

const getCustomFilterListIds = <T extends string>(customFilterList: Record<T, T | T[]>) => {
  const filterValues = objectEntries(customFilterList).map(([key, value]) => ({
    id: key,
    values: arrayWrap(value),
  }));
  if (filterValues.length > 0) {
    return { filterValues };
  }
  return null;
};

const parseActive = (queryParamActive: string | undefined): boolean => {
  // <SearchPanel /> components is responsible for setting of `activeIds`
  // and it's not clear if it's still required or not ...
  if (localStorage.getItem('activeIds') === 'true') {
    localStorage.removeItem('activeIds');
    return false;
  }
  return queryParamActive === 'true';
};

const ProductList: React.FC<ProductListProps> = ({ sorting, onSortingChange }) => {
  const {
    search = '',
    category,
    brands,
    sortingcolumn = 'name',
    sortingorder = 'ascend',
    pricev,
    matches,
    sites,
    active = 0,
    noMatch = 0,
    insight = 0,
    alert = 0,
    matchTypeFilter = 'ALL',
    region = '-1',
    priceType = '2',
    back = false,
    ...otherParams
  } = useQueryUrlParams();

  const sourceOwnerId = useSourceOwnerId();
  const { advancedFilters } = useSearchProductsStateContext();

  const pageVal = Number(localStorage.getItem('pageVal')) || 1;
  const pageInt = back && pageVal ? pageVal : PAGE_NUMBER;
  const [page, setPage] = useState(pageInt);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const ownerIds = sites ? arrayWrap(sites) : null;
  const categoryId = category ? arrayWrap(category).at(-1) : '';
  const manufacturerIds = brands ? arrayWrap(brands) : null;
  const priceVarianceIds = pricev ? arrayWrap(pricev) : null;
  const matchesIds = matches ? arrayWrap(matches) : null;
  const customFilterList = CustomFilters.extractQueryParams(otherParams);
  const customFilterListIds = getCustomFilterListIds(customFilterList);

  useEffect(() => {
    setPage(pageInt);
  }, [
    search,
    category,
    brands,
    pricev,
    matches,
    sortingcolumn,
    sortingorder,
    pageSize,
    sites,
    active,
    noMatch,
    insight,
    alert,
    matchTypeFilter,
    region,
    priceType,
  ]);

  const {
    data: products,
    loading: productsLoading,
    error: productsError,
    total: totalProducts,
  } = useProductListFetch(
    sorting,
    page,
    pageSize,
    search,
    categoryId,
    JSON.stringify(manufacturerIds),
    JSON.stringify(priceVarianceIds),
    JSON.stringify(matchesIds),
    JSON.stringify(ownerIds),
    sourceOwnerId,
    Number(parseActive(active)),
    Number(noMatch === 'true'),
    Number(insight === 'true'),
    Number(alert === 'true'),
    JSON.stringify(customFilterListIds),
    matchTypeFilter,
    region,
    priceType
  );
  const fetchLoading = productsLoading && products === null;
  return (
    <div className={clsx(styles.container, { [styles.withFilters]: advancedFilters?.isOpen })}>
      {productsError && (
        <Alert
          message="Error"
          description="An error has occurred when trying to get product list! Please try again later!"
          type="error"
          showIcon
        />
      )}
      {fetchLoading ? (
        <div className={styles.loadingIndicator}>
          <Spin spinning={fetchLoading} />
        </div>
      ) : (
        products && (
          <ProductListTable
            items={products}
            sorting={sorting}
            loading={productsLoading}
            pageSize={pageSize}
            page={page}
            total={totalProducts}
            onPageSizeChange={setPageSize}
            onPageChange={setPage}
            onSortingChange={onSortingChange}
          />
        )
      )}
    </div>
  );
};

export default ProductList;
